import { ChevronRightIcon, LockKeyholeIcon, SearchIcon, SparklesIcon } from "lucide-react";
import { useEffect, useRef } from "react";

import { Skeleton } from "@/components/ui/skeleton";
import type { SearchWordItem } from "@/types/common";

/* ── 검색어 하이라이트 ── */

function Highlight({ text, query }: { text: string; query: string }) {
  if (!query) {
    return <>{text}</>;
  }

  const idx = text.toLowerCase().indexOf(query.toLowerCase());

  if (idx === -1) {
    return <>{text}</>;
  }

  return (
    <>
      {text.slice(0, idx)}
      <span className="text-primary font-semibold">{text.slice(idx, idx + query.length)}</span>
      {text.slice(idx + query.length)}
    </>
  );
}

/* ── 검색 결과 아이템 ── */

function SearchResultItem({
  item,
  isLocked,
  query,
  onLockedClick,
}: {
  item: SearchWordItem;
  isLocked: boolean;
  query: string;
  onLockedClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={isLocked ? onLockedClick : undefined}
      className="w-full text-left px-5 py-3.5 flex items-center gap-3 transition-colors hover:bg-muted/50 active:bg-muted"
    >
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-1.5">
          <span className={`text-[15px] font-semibold ${isLocked ? "text-foreground/40" : "text-foreground"}`}>
            {isLocked ? item.word : <Highlight text={item.word} query={query} />}
          </span>

          {isLocked && <LockKeyholeIcon className="size-3.5 text-foreground/25" />}
        </div>

        <p
          className={`text-[13px] mt-0.5 truncate ${
            isLocked ? "text-foreground/20 blur-xs select-none" : "text-muted-foreground"
          }`}
        >
          {isLocked ? item.meaning : <Highlight text={item.meaning} query={query} />}
        </p>
      </div>

      {/* 카테고리 태그 */}
      <span
        className={`shrink-0 text-[11px] font-medium px-2 py-0.5 rounded-md ${
          isLocked ? "bg-primary/8 text-primary/70" : "bg-muted text-muted-foreground"
        }`}
      >
        {item.setTitle}
      </span>
    </button>
  );
}

/* ── Plus 업셀 배너 ── */

function PlusUpselBanner({ lockedCount, onClick }: { lockedCount: number; onClick: () => void }) {
  if (lockedCount === 0) {
    return null;
  }

  return (
    <button
      type="button"
      onClick={onClick}
      className="w-full flex items-center gap-3 px-5 py-3.5 bg-primary-background border-t border-primary/10 transition-colors hover:bg-primary-background/80"
    >
      <div className="size-8 rounded-full bg-gradient-to-br from-primary to-blue-500 flex items-center justify-center shrink-0">
        <SparklesIcon className="size-4 text-white" />
      </div>

      <div className="flex-1 text-left">
        <p className="text-[13px] font-bold text-primary">Plus 전용 단어 {lockedCount}개 더 보기</p>
        <p className="text-[11px] text-primary/60 mt-0.5">잠금 해제하고 모든 단어를 학습하세요</p>
      </div>

      <ChevronRightIcon className="size-4 text-primary/40 shrink-0" />
    </button>
  );
}

/* ── 빈 상태 ── */

function EmptySearch({ query }: { query: string }) {
  return (
    <div className="flex flex-col items-center justify-center py-16 px-6 gap-2">
      <SearchIcon className="size-10 text-muted-foreground/30" />
      <p className="text-sm font-semibold text-muted-foreground">검색 결과 없음</p>
      <p className="text-xs text-muted-foreground/60">"{query}"에 대한 결과가 없습니다</p>
    </div>
  );
}

/* ── 스켈레톤 ── */

function SearchSkeleton() {
  return (
    <div className="flex flex-col divide-y divide-border/50">
      {Array.from({ length: 10 }, (_, i) => (
        <div key={i} className="flex items-center gap-3 px-5 py-3.5">
          <div className="flex-1 flex flex-col gap-2">
            <Skeleton className="h-4 w-24" />
            <Skeleton className="h-3.5 w-40" />
          </div>
          <Skeleton className="h-5 w-16 rounded-md" />
        </div>
      ))}
    </div>
  );
}

/* ── 다음 페이지 로드 트리거 ── */

function LoadMoreTrigger({ onLoadMore, isFetching }: { onLoadMore: () => void; isFetching: boolean }) {
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const el = ref.current;

    if (!el) {
      return;
    }

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting && !isFetching) {
          onLoadMore();
        }
      },
      { rootMargin: "200px" },
    );

    observer.observe(el);

    return () => observer.disconnect();
  }, [onLoadMore, isFetching]);

  return (
    <div ref={ref} className="flex justify-center py-4">
      {isFetching && <div className="size-5 border-2 border-primary/30 border-t-primary rounded-full animate-spin" />}
    </div>
  );
}

/* ── 메인 SearchResults ── */

type SearchResultsProps = {
  results: SearchWordItem[];
  query: string;
  isPlus: boolean;
  isPending: boolean;
  hasNextPage: boolean;
  isFetchingNextPage: boolean;
  onFetchNextPage: () => void;
  onPaywallOpen: () => void;
};

export function SearchResults({
  results,
  query,
  isPlus,
  isPending,
  hasNextPage,
  isFetchingNextPage,
  onFetchNextPage,
  onPaywallOpen,
}: SearchResultsProps) {
  if (isPending) {
    return (
      <>
        <div className="px-5 pt-2.5 pb-1 min-h-[38px] flex items-center">
          <Skeleton className="h-3.5 w-15.5" />
        </div>
        <SearchSkeleton />
      </>
    );
  }

  if (results.length === 0) {
    return <EmptySearch query={query} />;
  }

  const lockedCount = results.filter(r => !r.isFree && !isPlus).length;

  return (
    <>
      {/* 결과 수 */}
      <div className="px-5 pt-2.5 pb-1">
        <span className="text-[11px] font-semibold text-muted-foreground/50 tracking-wide">
          {results.length}개 결과
        </span>
      </div>

      {/* 결과 목록 — 잠금/비잠금 혼합 */}
      <div className="divide-y divide-border/50">
        {results.map(item => {
          const isLocked = !item.isFree && !isPlus;

          return (
            <SearchResultItem
              key={item.id}
              item={item}
              isLocked={isLocked}
              query={query}
              onLockedClick={onPaywallOpen}
            />
          );
        })}
      </div>

      {/* 무한 스크롤 트리거 */}
      {hasNextPage && <LoadMoreTrigger onLoadMore={onFetchNextPage} isFetching={isFetchingNextPage} />}

      {/* 하단 업셀 배너 (비구독자 + 더 불러올 페이지 없을 때) */}
      {!isPlus && !hasNextPage && <PlusUpselBanner lockedCount={lockedCount} onClick={onPaywallOpen} />}
    </>
  );
}
